/**
 * BMT Companion DEV-ONLY harness — a localStorage-backed fake of the trainee
 * routes (/api/v2/training/bmt/*), so every BMT screen can be seen in a browser
 * with no backend, no Auth0 and no intake set up by LDT.
 *
 * Same shape as src/utils/voucher-harness.js: a toggle plus a state blob, gated
 * on non-production here AND in the store that consumes it (store/modules/bmt.js),
 * and driven from the /bmt-mock console, a route that production builds do not
 * contain.
 *
 * Fixtures are generated relative to today (Singapore time), so "check-in opens
 * at 6:00 pm" stays true however long this file sits in the repo. No real member
 * data: everything here is invented.
 */

export const BMT_HARNESS_KEY = 'bmtHarnessState';

export const BMT_HARNESS_SCENARIOS = Object.freeze({
  NOT_ENROLLED: 'not-enrolled',
  PENDING: 'pending',
  APPROVED: 'approved',
  WITHDRAWN: 'withdrawn',
});

/** What the upcoming day's check-in window is doing in the approved scenario. */
export const BMT_HARNESS_CHECKIN = Object.freeze({
  NOT_YET: 'not-yet',
  OPEN: 'open',
  CLOSED: 'closed',
});

/** A missed date on the approved trainee's course, and where its make-up stands. */
export const BMT_HARNESS_MAKE_UP = Object.freeze({
  NONE: 'none',
  WAITING: 'waiting',
  OPEN: 'open',
  BOOKED: 'booked',
});

const isProd = () => process.env.VUE_APP_NODE_ENV === 'production';

const SGT_OFFSET_HOURS = 8;
const DAY_MS = 24 * 60 * 60 * 1000;

// ── Time helpers (Singapore calendar) ─────────────────────────────────────────

/** The Singapore calendar date `offset` days from today, as YYYY-MM-DD. */
export const sgDate = (offset = 0, now = new Date()) =>
  new Date(now.getTime() + SGT_OFFSET_HOURS * 3600 * 1000 + offset * DAY_MS)
    .toISOString()
    .slice(0, 10);

/** A Singapore wall-clock time on a Singapore date, as a UTC instant. */
export const sgInstant = (date, hhmm) => {
  const [y, m, d] = date.split('-').map(Number);
  const [h, min] = hhmm.split(':').map(Number);
  return new Date(Date.UTC(y, m - 1, d, h - SGT_OFFSET_HOURS, min)).toISOString();
};

const addMinutes = (iso, minutes) => new Date(Date.parse(iso) + minutes * 60 * 1000).toISOString();

// ── Fixture builders ──────────────────────────────────────────────────────────

const TOPICS = [
  ['Introduction to BMT', 'The Word of God'],
  ['Prayer', 'Serving in Church'],
  ['Generosity', 'Building Relationships'],
  ['Sharing Your Faith', 'Commissioning'],
];

let sessionSeq = 800;

const ZOOM_URL = 'https://zoom.us/j/0000000000';

function buildDay(
  date,
  { start, end, venue, topics, checkin = 'not-yet', statuses = [], online = false },
) {
  const startsAt = sgInstant(date, start);
  const endsAt = sgInstant(date, end);
  const opensAt = addMinutes(startsAt, -60);
  const closesAt = addMinutes(endsAt, 30);
  return {
    date,
    startsAt,
    endsAt,
    venue: online ? null : venue,
    online,
    meetingUrl: online ? ZOOM_URL : null,
    checkin: online
      ? { state: 'none', opensAt: null, closesAt: null }
      : { state: checkin, opensAt, closesAt },
    sessions: topics.map((title, i) => {
      sessionSeq += 1;
      const midpoint = addMinutes(startsAt, i * 75);
      const status = statuses[i] || { status: 'upcoming', attendedOn: null };
      return {
        id: sessionSeq,
        title,
        // One fixture topic carries a schedule note, to show how one looks.
        note: title === 'Commissioning' ? 'Students only' : null,
        startsAt: midpoint,
        endsAt: addMinutes(midpoint, 70),
        ...status,
      };
    }),
  };
}

const INTAKES = {
  41: { label: 'BMT', start: '19:30', end: '22:00', venue: 'Hall 2', firstOffset: 12 },
  42: { label: 'BMT Youth', start: '14:00', end: '16:30', venue: 'Youth Hall', firstOffset: 13 },
};

/** Four weekly days, all still to come. */
function upcomingDays(intakeId) {
  const intake = INTAKES[intakeId];
  return TOPICS.map((topics, week) =>
    buildDay(sgDate(intake.firstOffset + week * 7), {
      start: intake.start,
      end: intake.end,
      venue: intake.venue,
      topics,
    }),
  );
}

/**
 * The approved trainee: one day behind them (both topics attended, one of them
 * made up on another date, or missed when the console asks for a make-up), the
 * next day's check-in in whichever state the console picked, and two more weeks
 * to come. `courseOver` moves every date into the past (MU4 / MU5).
 */
function approvedDays(checkin, { makeUp = BMT_HARNESS_MAKE_UP.NONE, courseOver = false } = {}) {
  const intake = INTAKES[41];
  const missed = makeUp !== BMT_HARNESS_MAKE_UP.NONE;
  const attended = [
    { status: 'attended', attendedOn: null },
    { status: 'attended', attendedOn: null },
  ];
  const offsets = courseOver ? [-26, -19, -12, -5] : [-5, null, 9, 16];
  const pastDate = sgDate(offsets[0]);
  const past = buildDay(pastDate, {
    start: intake.start,
    end: intake.end,
    venue: intake.venue,
    topics: TOPICS[0],
    checkin: 'closed',
    statuses: missed
      ? [
          { status: 'missed', attendedOn: null },
          { status: 'missed', attendedOn: null },
        ]
      : [attended[0], { status: 'attended', attendedOn: sgDate(-3) }],
  });
  if (courseOver) {
    const rest = [1, 2, 3].map(i =>
      buildDay(sgDate(offsets[i]), {
        start: intake.start,
        end: intake.end,
        venue: intake.venue,
        topics: TOPICS[i],
        checkin: 'closed',
        statuses: attended,
        online: i === 2,
      }),
    );
    return [past, ...rest];
  }
  // Open or closed only makes sense today; not-yet is a couple of days out.
  const nextDate = checkin === BMT_HARNESS_CHECKIN.NOT_YET ? sgDate(2) : sgDate(0);
  const next = buildDay(nextDate, {
    start: intake.start,
    end: intake.end,
    venue: intake.venue,
    topics: TOPICS[1],
    checkin,
  });
  const later = [2, 3].map(i =>
    buildDay(sgDate(2 + (i - 1) * 7), {
      start: intake.start,
      end: intake.end,
      venue: intake.venue,
      topics: TOPICS[i],
      // The third date is on Zoom: no check-in, and its videos open when it starts.
      online: i === 2,
    }),
  );
  return [past, next, ...later];
}

/** The next BMT's date that repeats the missed topics, as a make-up option. */
function makeUpOption(missedDay) {
  const intake = INTAKES[41];
  const date = sgDate(60);
  const day = buildDay(date, {
    start: intake.start,
    end: intake.end,
    venue: intake.venue,
    topics: missedDay.sessions.map(session => session.title),
  });
  return {
    intakeId: 51,
    date,
    startsAt: day.startsAt,
    endsAt: day.endsAt,
    venue: day.venue,
    online: false,
    checkin: day.checkin,
    sessions: day.sessions.map(({ id, title, startsAt, endsAt }) => ({
      id,
      title,
      startsAt,
      endsAt,
    })),
  };
}

function makeUpsFor(state, days) {
  if (state.scenario !== BMT_HARNESS_SCENARIOS.APPROVED) return [];
  const makeUp = state.makeUp || BMT_HARNESS_MAKE_UP.NONE;
  if (makeUp === BMT_HARNESS_MAKE_UP.NONE) return [];
  const [missedDay] = days;
  const option = makeUpOption(missedDay);
  const booked = makeUp === BMT_HARNESS_MAKE_UP.BOOKED || Boolean(state.makeUpBooked);
  const open = makeUp !== BMT_HARNESS_MAKE_UP.WAITING;
  return [
    {
      missedDate: missedDay.date,
      topics: missedDay.sessions.map(session => session.title),
      status: booked ? 'booked' : open ? 'open' : 'waiting',
      options: open ? [option] : [],
      booked: booked ? option : null,
    },
  ];
}

function homeworkFixtures(days) {
  const [first] = days;
  const firstSession = first?.sessions?.[0] || { id: 801, title: TOPICS[0][0] };
  const secondSession = first?.sessions?.[1] || { id: 802, title: TOPICS[0][1] };
  const from = session => ({
    id: session.id,
    title: session.title,
    date: first?.date || sgDate(-5),
  });
  const blank = { submitted: false, submittedAt: null, text: null, sections: null };
  return [
    {
      id: 12,
      kind: 'MINISTRY_APPLICATION',
      type: 'TICK',
      title: 'Ministry Application',
      linkUrl: 'https://www.heartofgodchurch.org',
      description: null,
      required: true,
      session: from(firstSession),
      ...blank,
    },
    {
      id: 13,
      kind: 'TESTIMONY',
      type: 'TEXT',
      title: 'Your testimony',
      linkUrl: null,
      description: null,
      required: false,
      session: from(secondSession),
      ...blank,
    },
    {
      id: 14,
      kind: 'VIDEO',
      type: 'TICK',
      title: 'John 3:16 Training',
      linkUrl: 'https://www.w3schools.com/html/mov_bbb.mp4',
      description: 'A short line on what this training covers.',
      required: false,
      session: from(firstSession),
      ...blank,
    },
  ];
}

// ── State ─────────────────────────────────────────────────────────────────────

const DEFAULT_STATE = {
  enabled: false,
  scenario: BMT_HARNESS_SCENARIOS.NOT_ENROLLED,
  checkin: BMT_HARNESS_CHECKIN.NOT_YET,
  needsCgCheck: true,
  /** The intake chosen on sign-up, or the fixture's own for pending/approved. */
  intakeId: 41,
  /** id → { submitted, submittedAt, text }, layered over the fixtures. */
  submissions: {},
  /** A pending walk-in with no CG, not yet asked about one. */
  needsCgAnswer: false,
  /** Approved scenario: a missed first date, and where its make-up stands. */
  makeUp: BMT_HARNESS_MAKE_UP.NONE,
  /** Approved scenario: every date is in the past. */
  courseOver: false,
  /** A make-up picked on the MU1 screen. */
  makeUpBooked: false,
  /** The dates ticked at sign-up, or null for all. */
  attendingDates: null,
  /** The Reflection: { areas, keptAt }. */
  reflection: null,
  /** Ministry Application handed in on the form (the API reads the forms service). */
  formSubmitted: false,
};

function readState() {
  try {
    const raw = localStorage.getItem(BMT_HARNESS_KEY);
    return raw ? { ...DEFAULT_STATE, ...JSON.parse(raw) } : { ...DEFAULT_STATE };
  } catch {
    return { ...DEFAULT_STATE };
  }
}

function writeState(state) {
  try {
    localStorage.setItem(BMT_HARNESS_KEY, JSON.stringify(state));
  } catch {
    // Storage unavailable: the harness simply does not persist across reloads.
  }
  return state;
}

export const getBmtHarnessState = () => readState();

export const isBmtHarnessEnabled = () => !isProd() && readState().enabled === true;

/** Turn the harness on in a given scenario, discarding any earlier submissions. */
export function seedBmtHarness(scenario, options = {}) {
  return writeState({
    ...DEFAULT_STATE,
    ...options,
    enabled: true,
    scenario,
    submissions: {},
  });
}

export const updateBmtHarness = patch => writeState({ ...readState(), ...patch });

export const disableBmtHarness = () => writeState({ ...readState(), enabled: false });

// ── The fake API ──────────────────────────────────────────────────────────────

/** An axios-shaped error carrying the API's `{ statusCode, code, message }` body. */
export function harnessApiError(status, code, message = code) {
  const error = new Error(message);
  error.response = { status, data: { statusCode: status, code, message } };
  return error;
}

function daysFor(state) {
  // Same ids on every read, so homework and a check-in result can point at /me's sessions.
  sessionSeq = 800;
  if (state.scenario === BMT_HARNESS_SCENARIOS.APPROVED) {
    return approvedDays(state.checkin, { makeUp: state.makeUp, courseOver: state.courseOver });
  }
  if (state.scenario === BMT_HARNESS_SCENARIOS.PENDING) return upcomingDays(state.intakeId);
  return [];
}

const ENROLMENT_STATUS = {
  [BMT_HARNESS_SCENARIOS.APPROVED]: 'APPROVED',
  [BMT_HARNESS_SCENARIOS.PENDING]: 'PENDING',
  [BMT_HARNESS_SCENARIOS.WITHDRAWN]: 'WITHDRAWN',
};

export function harnessMe(now = new Date()) {
  const state = readState();
  const enrolled = state.scenario !== BMT_HARNESS_SCENARIOS.NOT_ENROLLED;
  const days = daysFor(state);
  const dates = days.map(day => day.date);
  const attendingDates = Array.isArray(state.attendingDates)
    ? dates.filter(date => state.attendingDates.includes(date))
    : dates;
  const firstStart = days[0]?.startsAt ? Date.parse(days[0].startsAt) : Infinity;
  return {
    serverTime: now.toISOString(),
    enrolment: enrolled
      ? {
          id: 7,
          intakeId: state.intakeId,
          label: INTAKES[state.intakeId]?.label || 'BMT',
          status: ENROLMENT_STATUS[state.scenario],
          needsCgAnswer:
            state.scenario === BMT_HARNESS_SCENARIOS.PENDING && state.needsCgAnswer === true,
          attendingDates,
          canChangeDates: firstStart > now.getTime(),
        }
      : null,
    needsCgCheck: enrolled ? state.needsCgAnswer === true : state.needsCgCheck,
    openIntakes: enrolled
      ? []
      : Object.keys(INTAKES).map(id => ({
          intakeId: Number(id),
          label: INTAKES[id].label,
          days: upcomingDays(Number(id)),
        })),
    days: days.map(day => ({ ...day, attending: attendingDates.includes(day.date) })),
    makeUps: makeUpsFor(state, days),
  };
}

/** Change my attendance: only before the first date begins. */
export function harnessChangeDates({ dates } = {}) {
  const me = harnessMe();
  if (!me.enrolment) throw harnessApiError(403, 'NOT_ENROLLED', 'Not signed up for BMT.');
  if (!me.enrolment.canChangeDates) {
    throw harnessApiError(409, 'DATES_LOCKED', 'BMT has started.');
  }
  if (!Array.isArray(dates) || !dates.length) {
    throw harnessApiError(422, 'DATES_REQUIRED', 'Tick at least one date.');
  }
  const own = me.days.map(day => day.date);
  if (dates.some(date => !own.includes(date))) {
    throw harnessApiError(422, 'INVALID_DATES', 'Not a date of this BMT.');
  }
  updateBmtHarness({ attendingDates: [...new Set(dates)] });
  return harnessMe();
}

/** Pick a make-up date (MU1). */
export function harnessBookMakeUp({ missedDate, intakeId, date } = {}) {
  const makeUp = harnessMe().makeUps.find(m => m.missedDate === missedDate);
  const ok = makeUp?.options.some(o => o.intakeId === intakeId && o.date === date);
  if (!ok) throw harnessApiError(409, 'MAKEUP_NOT_AVAILABLE', 'Not a make-up date.');
  updateBmtHarness({ makeUpBooked: true });
  return harnessMe();
}

export function harnessEnrol({ intakeId, cgNote, dates } = {}) {
  const state = readState();
  if (state.scenario !== BMT_HARNESS_SCENARIOS.NOT_ENROLLED) {
    if (state.intakeId === intakeId) return harnessMe();
    throw harnessApiError(409, 'ALREADY_ENROLLED', 'Already signed up for BMT.');
  }
  if (!INTAKES[intakeId]) {
    throw harnessApiError(409, 'INTAKE_NOT_OPEN', 'This intake is not open for sign-up.');
  }
  if (state.needsCgCheck && !cgNote) {
    throw harnessApiError(422, 'CG_NOTE_REQUIRED', 'Tell us about your Connect Group.');
  }
  // The API approves role I and above with a CG straight away; anyone it has to
  // ask about a CG waits for the BMT team.
  if (Array.isArray(dates) && !dates.length) {
    throw harnessApiError(422, 'DATES_REQUIRED', 'Tick at least one date.');
  }
  writeState({
    ...state,
    intakeId,
    attendingDates: Array.isArray(dates) ? dates : null,
    scenario: state.needsCgCheck ? BMT_HARNESS_SCENARIOS.PENDING : BMT_HARNESS_SCENARIOS.APPROVED,
  });
  return harnessMe();
}

export function harnessAnswerCg({ cgNote } = {}) {
  if (!cgNote?.trim()) {
    throw harnessApiError(422, 'CG_NOTE_REQUIRED', 'Tell us about your Connect Group.');
  }
  updateBmtHarness({ needsCgAnswer: false });
  return harnessMe();
}

/** Every date's videos are open from the start, except a date missed and not yet made up. */
export function harnessResources() {
  const state = readState();
  if (state.scenario !== BMT_HARNESS_SCENARIOS.APPROVED) {
    throw harnessApiError(403, 'NOT_APPROVED', 'Waiting for the BMT team.');
  }
  let id = 900;
  const item = (kind, title, session = null) => {
    id += 1;
    return {
      id,
      kind,
      title,
      url: 'https://www.heartofgodchurch.org',
      session,
      thumbnailUrl: null,
    };
  };
  const days = daysFor(state).map(day => {
    const missed = day.sessions.some(session => session.status === 'missed');
    return {
      date: day.date,
      online: day.online,
      sessionCount: day.sessions.length,
      unlocked: !missed,
      opensAt: day.online ? day.startsAt : null,
      items: missed
        ? []
        : day.sessions.map(session =>
            item('VIDEO', session.title, { id: session.id, title: session.title, date: day.date }),
          ),
    };
  });
  return {
    general: [item('LINK', 'YouVersion'), item('LINK', 'HOGC One App')],
    days,
  };
}

export function harnessHomework() {
  const state = readState();
  if (
    state.scenario === BMT_HARNESS_SCENARIOS.NOT_ENROLLED ||
    state.scenario === BMT_HARNESS_SCENARIOS.WITHDRAWN
  ) {
    throw harnessApiError(403, 'NOT_ENROLLED', 'Not signed up for BMT.');
  }
  if (state.scenario === BMT_HARNESS_SCENARIOS.PENDING) {
    throw harnessApiError(403, 'NOT_APPROVED', 'Waiting for the BMT team.');
  }
  return homeworkFixtures(daysFor(state)).map(item => {
    const own = state.submissions[item.id] || {};
    // The API reads the Ministry Application off the forms service: done once the form is in.
    if (item.kind === 'MINISTRY_APPLICATION' && state.formSubmitted && !own.submitted) {
      return { ...item, submitted: true, submittedAt: null };
    }
    return { ...item, ...own };
  });
}

export function harnessSubmit(id, { text, sections } = {}) {
  const items = harnessHomework();
  const item = items.find(h => h.id === id);
  if (!item) throw harnessApiError(404, 'HOMEWORK_NOT_FOUND', 'Homework not found.');
  if (item.kind === 'MINISTRY_APPLICATION') {
    throw harnessApiError(422, 'NOT_SELF_REPORTED', 'Ticks itself once the form is in.');
  }
  if (item.kind === 'TESTIMONY') {
    if (!Array.isArray(sections) || sections.length !== 4 || sections.some(part => !part?.trim())) {
      throw harnessApiError(422, 'SECTIONS_REQUIRED', 'Fill in all 4 sections.');
    }
  } else if (item.type === 'TEXT' && !text?.trim()) {
    throw harnessApiError(422, 'TEXT_REQUIRED', 'Text is required.');
  }
  const state = readState();
  writeState({
    ...state,
    submissions: {
      ...state.submissions,
      [id]: {
        submitted: true,
        submittedAt: new Date().toISOString(),
        text:
          item.kind === 'TESTIMONY' ? sections.join('\n\n') : item.type === 'TEXT' ? text : null,
        sections: item.kind === 'TESTIMONY' ? sections : null,
      },
    },
  });
  return harnessHomework();
}

/** The private Reflection. */
export function harnessReflection() {
  if (readState().scenario !== BMT_HARNESS_SCENARIOS.APPROVED) {
    throw harnessApiError(403, 'NOT_APPROVED', 'Waiting for the BMT team.');
  }
  return readState().reflection || { areas: [], keptAt: null };
}

export function harnessSaveReflection({ areas } = {}) {
  harnessReflection();
  if (!Array.isArray(areas) || !areas.length || areas.length > 6) {
    throw harnessApiError(422, 'INVALID_REFLECTION', 'Pick 1 to 6 areas.');
  }
  const reflection = { areas, keptAt: new Date().toISOString() };
  updateBmtHarness({ reflection });
  return reflection;
}

export function harnessClearReflection() {
  updateBmtHarness({ reflection: null });
  return { areas: [], keptAt: null };
}

export function harnessUnsubmit(id) {
  const state = readState();
  writeState({
    ...state,
    submissions: {
      ...state.submissions,
      [id]: { submitted: false, submittedAt: null, text: null, sections: null },
    },
  });
  return harnessHomework();
}

// ── Check-in results (the /bmt/check-in screen) ───────────────────────────────

/** The outcomes the console can show, as `bmt.lastCheckIn` would hold them. */
export const BMT_HARNESS_RESULTS = Object.freeze({
  CHECKED_IN: 'checked-in',
  CHECKED_IN_PENDING: 'checked-in-pending',
  ALREADY: 'already',
  NOT_OPEN: 'not-open',
  CLOSED: 'closed',
  UNAVAILABLE: 'unavailable',
  WITHDRAWN: 'withdrawn',
  OFFLINE: 'offline',
  ERROR: 'error',
});

/**
 * A `bmt.lastCheckIn` fixture for one outcome, against today's fixture day
 * (the approved trainee's, so its homework lines up), or null for none.
 */
export function harnessCheckInResult(kind, now = new Date()) {
  const today = sgDate(0, now);
  sessionSeq = 800;
  const day = approvedDays(BMT_HARNESS_CHECKIN.OPEN).find(d => d.date === today);
  const sessions = (day?.sessions || []).map(({ id, title }) => ({ id, title }));
  const checkedInAt = addMinutes(day?.startsAt || now.toISOString(), -12);
  const checkIn = (overrides = {}) => ({
    alreadyCheckedIn: false,
    date: today,
    sessions,
    enrolmentStatus: 'APPROVED',
    checkedInAt,
    ...overrides,
  });
  const success = (code, data) => ({
    status: 'success',
    code,
    display: { title: 'Checked in' },
    checkIn: data,
    opensAt: null,
  });
  const failure = (code, message, opensAt = null) => ({
    status: 'error',
    code,
    display: { title: 'Check-in', message },
    checkIn: null,
    opensAt,
  });
  switch (kind) {
    case BMT_HARNESS_RESULTS.CHECKED_IN:
      return success('CHECKED_IN', checkIn());
    case BMT_HARNESS_RESULTS.CHECKED_IN_PENDING:
      return success('CHECKED_IN', checkIn({ enrolmentStatus: 'PENDING' }));
    case BMT_HARNESS_RESULTS.ALREADY:
      return success('ALREADY_CHECKED_IN', checkIn({ alreadyCheckedIn: true }));
    case BMT_HARNESS_RESULTS.NOT_OPEN:
      return failure(
        'CHECKIN_NOT_OPEN',
        'Check-in is not open yet.',
        addMinutes(now.toISOString(), 25),
      );
    case BMT_HARNESS_RESULTS.CLOSED:
      return failure('CHECKIN_CLOSED', 'Check-in has closed.');
    case BMT_HARNESS_RESULTS.UNAVAILABLE:
      return failure('SESSION_NOT_AVAILABLE', 'This session is not open for check-in.');
    case BMT_HARNESS_RESULTS.WITHDRAWN:
      return success('CHECKED_IN', checkIn({ enrolmentStatus: 'WITHDRAWN' }));
    case BMT_HARNESS_RESULTS.OFFLINE:
      return { status: 'error', code: null, display: null, checkIn: null, opensAt: null };
    case BMT_HARNESS_RESULTS.ERROR:
      return failure('SCAN_FAILED', 'Something went wrong with that scan.');
    default:
      return null;
  }
}
